import { Loader2, AlertTriangle, FlaskConical, Inbox } from 'lucide-react';
import type { ConnectorStatus } from '../../shared/contracts';
import { hideDemoBadges } from '../lib/flags';

export function Loader({ label = 'Henter data …' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-24 text-brand-dark/50">
      <Loader2 className="animate-spin" size={18} />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-24 text-center">
      <AlertTriangle className="text-brand-error" size={24} />
      <p className="text-sm text-brand-dark/70">Kunne ikke hente data fra serveren.</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-brand bg-brand-primary px-4 py-2 text-sm text-white hover:bg-brand-primary-hover"
        >
          Prøv igen
        </button>
      )}
    </div>
  );
}

/** Shown when no connector is live — makes the demo state explicit and honest. */
export function DemoBanner({ connectors }: { connectors: ConnectorStatus[] }) {
  const mock = connectors.filter((c) => c.mode === 'mock').map((c) => c.label);
  if (mock.length === 0 || hideDemoBadges()) return null;
  return (
    <div className="mb-5 flex items-center gap-2.5 rounded-brand border border-brand-amber/25 bg-brand-amber-light/50 px-4 py-2">
      <FlaskConical size={14} className="shrink-0 text-brand-amber" />
      <p className="text-xs text-brand-dark/70">
        <span className="font-medium">Demodata</span> for {mock.join(', ')} — tilføj API-nøgler i backendens{' '}
        <code className="rounded bg-white/70 px-1">.env</code> for live tal.
      </p>
    </div>
  );
}

/** Shared empty-state row for the list-style cards. */
export function EmptyRow({ label, className = '' }: { label: string; className?: string }) {
  return <li className={`py-4 text-sm text-brand-dark/40 ${className}`}>{label}</li>;
}

/** Inline error state for a single pane whose source failed in production. */
export function PaneError({ reason }: { reason?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-8 text-center">
      <AlertTriangle className="text-brand-error" size={20} />
      <p className="text-xs text-brand-dark/60">{reason ?? 'Kunne ikke hente data fra kilden.'}</p>
    </div>
  );
}

/** Inline empty state for a single pane whose source returned no data. */
export function PaneEmpty({ label = 'Ingen data i perioden.' }: { label?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-8 text-center">
      <Inbox className="text-brand-dark/30" size={20} />
      <p className="text-xs text-brand-dark/50">{label}</p>
    </div>
  );
}

export function ConnectorPills({ connectors }: { connectors: ConnectorStatus[] }) {
  // In presentation mode the grey "mock" dots would betray the demo — hide the
  // whole status row (set VITE_HIDE_DEMO_BADGES only when nothing should read as demo).
  if (hideDemoBadges()) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {connectors.map((c) => (
        <span
          key={c.system}
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] ${
            c.mode === 'live'
              ? 'bg-brand-green-light text-brand-green'
              : 'bg-brand-primary/5 text-brand-dark/55'
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${c.mode === 'live' ? 'bg-brand-green' : 'bg-brand-dark/30'}`} />
          {c.label}
        </span>
      ))}
    </div>
  );
}
